// YNX — PWA bootstrap (service worker + Android install prompt + iOS hint).
// All flows are fail-silent and never block the React render.

const SW_PATH = '/service-worker.js';
const LS_DISMISSED = 'ynx_pwa_install_dismissed';

let _deferredPrompt = null;
const _listeners = new Set();

/** Register the SW. Idempotent. */
export const initPWA = () => {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;
  // Wait for "load" to avoid competing with hydration
  window.addEventListener('load', () => {
    navigator.serviceWorker.register(SW_PATH).catch((err) => {
      // eslint-disable-next-line no-console
      console.warn('YNX PWA: SW registration failed', err);
    });
  });
};

/** Listen for the BeforeInstallPrompt event (Chrome/Edge/Brave/Samsung). */
export const registerInstallPrompt = () => {
  if (typeof window === 'undefined') return;
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    _deferredPrompt = e;
    _listeners.forEach((cb) => {
      try { cb(true); } catch (err) { console.warn('YNX PWA: listener failed', err); }
    });
  });
  window.addEventListener('appinstalled', () => {
    _deferredPrompt = null;
    _listeners.forEach((cb) => {
      try { cb(false); } catch (err) { console.warn('YNX PWA: listener (uninstall) failed', err); }
    });
  });
};

/** Subscribe to install-availability changes. Returns unsubscribe fn. */
export const onInstallAvailability = (cb) => {
  _listeners.add(cb);
  // Fire immediately with current state
  try { cb(!!_deferredPrompt); }
  catch (err) { console.warn('YNX PWA: initial availability callback failed', err); }
  return () => _listeners.delete(cb);
};

/** Trigger the native install prompt. Returns 'accepted' | 'dismissed' | null. */
export const promptInstall = async () => {
  if (!_deferredPrompt) return null;
  const p = _deferredPrompt;
  _deferredPrompt = null;
  try {
    p.prompt();
    const choice = await p.userChoice;
    _listeners.forEach((cb) => {
      try { cb(false); } catch (err) { console.warn('YNX PWA: listener (prompted) failed', err); }
    });
    return choice?.outcome || null;
  } catch (err) {
    console.warn('YNX PWA: install prompt failed', err);
    return null;
  }
};

export const dismissInstall = () => {
  try { localStorage.setItem(LS_DISMISSED, String(Date.now())); }
  catch (err) { console.debug('YNX PWA: localStorage write blocked', err); }
};

/** True if user dismissed within the last 30 days. */
export const wasRecentlyDismissed = () => {
  try {
    const ts = parseInt(localStorage.getItem(LS_DISMISSED) || '0', 10);
    if (!ts) return false;
    return (Date.now() - ts) < 30 * 86400 * 1000;
  } catch (err) {
    console.debug('YNX PWA: localStorage read blocked', err);
    return false;
  }
};

/** Detect iOS so we can show the "Ajouter à l’écran d’accueil" hint. */
export const isIOS = () => {
  const ua = navigator.userAgent || '';
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
};

/** Already installed (running in standalone)? */
export const isStandalone = () => {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
};
